import { t } from 'i18next';
import type { ErrorHandlerFunction } from '@/api/generator';
import type { NotificationMessage } from '@/components/notifications/types';
import { useNotificationStore } from '@/stores/notificationStore';
import { ref, type Ref } from 'vue';
import { extractErrorMessage } from './errorUtils';
import { AnyApiError, isKnownApiError } from './typeGuards';

export interface ErrorNotifyOptions {
  showNotificationInToast: boolean;
  notificationMessage: Ref<NotificationMessage | undefined>;
  closable: boolean;
}

export function useStoreErrorNotification(
  notifyOptions: ErrorNotifyOptions = {
    showNotificationInToast: true,
    notificationMessage: ref(),
    closable: false,
  },
) {
  const errorHandler: ErrorHandlerFunction<AnyApiError> = (error) => {
    // A known API error's message is translated via its i18n key, falling back to the exception
    // message and then a generic default; an inner exception, when present, takes priority over
    // all of that. Everything else (fetch failures, undeclared statuses, unwrapped errors) shows
    // the error's own message as-is.
    const known = isKnownApiError(error);
    const inner = known ? error.error?.exception?.innerException : undefined;
    let message: string;
    if (inner) message = inner.message ?? '';
    else if (known) message = extractErrorMessage(error);
    else message = error.message ?? '';

    notifyOptions.notificationMessage.value = {
      type: 'error',
      title: t('ErrorTitle'),
      message,
      closable: notifyOptions.closable,
    };

    if (notifyOptions.showNotificationInToast)
      useNotificationStore().notify(notifyOptions.notificationMessage.value, { show: false });

    // Returning the error keeps the chain going; append `stopErrorPropagation` to stop it.
    return error;
  };

  const clearError = () => {
    notifyOptions.notificationMessage.value = undefined;
  };

  return {
    errorHandler,
    notificationMessage: notifyOptions.notificationMessage,
    clearError,
  };
}
